import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Divider } from 'antd';
import { UserOutlined, LockOutlined, SecurityScanOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

const { Title, Text } = Typography;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('credentials');
  const [userId, setUserId] = useState(null);
  const [code, setCode] = useState('');
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { login, verify2fa } = useAuthStore();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_expired') === '1') {
      message.warning('Your session has expired. Please login again.');
      window.history.replaceState({}, document.title, '/');
    }
  }, []);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const result = await login({
        login: values.email,
        password: values.password,
        user_type: 'provider',
      });

      if (result.requires_2fa) {
        setUserId(result.user_id);
        setStep('verify');
      } else if (result.success) {
        message.success('Login successful!');
        navigate('/');
      } else {
        message.error(result.error || 'Login failed');
      }
    } catch (error) {
      message.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async () => {
    if (!code || code.length !== 6) {
      message.warning('Please enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const result = await verify2fa(userId, code);
      if (result.success) {
        message.success('Login successful!');
        navigate('/');
      } else {
        message.error(result.error || 'Verification failed');
      }
    } catch (error) {
      message.error('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img
            src="/images/kankali-logo.png"
            alt="Kankali Mart"
            style={{ height: '60px', width: 'auto', marginBottom: 12 }}
            onError={(e) => {
              e.target.src =
                'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Crect width=%22100%22 height=%22100%22 rx=%2220%22 fill=%22%232d5a3d%22/%3E%3Ctext x=%2250%22 y=%2268%22 font-family=%22Arial,sans-serif%22 font-size=%2244%22 font-weight=%22bold%22 fill=%22white%22 text-anchor=%22middle%22%3ECC%3C/text%3E%3C/svg%3E';
            }}
          />
          {step === 'credentials' ? (
            <>
              <Title level={3} style={{ margin: 0, color: '#262626' }}>
                Provider Login
              </Title>
              <Text type="secondary">Kankali Mart Provider Portal</Text>
            </>
          ) : (
            <>
              <Title level={3} style={{ margin: 0, color: '#262626' }}>
                Two-Factor Auth
              </Title>
              <Text type="secondary">Enter the code from your authenticator app</Text>
            </>
          )}
        </div>

        {step === 'credentials' ? (
          <Form form={form} name="login" onFinish={handleSubmit} layout="vertical" size="large">
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter your email!' },
                { type: 'email', message: 'Please enter a valid email!' },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Enter your email" />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please enter your password!' }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Enter your password" />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{ width: '100%', height: '48px' }}
              >
                Sign In
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <SecurityScanOutlined style={{ fontSize: 48, color: '#667eea', marginBottom: 16 }} />
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              maxLength={6}
              placeholder="000000"
              style={{
                textAlign: 'center',
                fontSize: 28,
                letterSpacing: 8,
                height: 56,
                borderRadius: 10,
                marginBottom: 16,
              }}
            />
            <Button
              type="primary"
              block
              size="large"
              loading={loading}
              onClick={handleVerify2fa}
              style={{ height: '48px' }}
            >
              Verify
            </Button>
            <div style={{ marginTop: 12 }}>
              <Button type="link" onClick={() => { setStep('credentials'); setCode(''); setUserId(null); }}>
                Back to login
              </Button>
            </div>
          </div>
        )}

        <Divider>
          <Text type="secondary">Provider Portal</Text>
        </Divider>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Need help? Contact support@kankalimart.com
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default Login;
